import { format } from "date-fns";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { PersonalApplicantInformation } from "Store/interfaces/PersonalApplicantInformation";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { AkaGroupLifeApplicantInsuranceSpecification } from "Store/interfaces/products/checkout/lifeProducts/AkaGroupLifeProduct";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { isDefined } from "Utils/functions";
import { Mutable } from "Utils/types";
import { getAkaGroupLifeCoverageGroup } from "./getters";
import {
  isAkaGroupLifeCoInsuredSelected,
  isAkaGroupLifeDeathCoInsuredSelected,
  isAkaGroupLifeDeathSelected,
  isAkaGroupLifeIncapacityCoInsuredSelected,
  isAkaGroupLifeIncapacitySelected,
  isAkaGroupLifeInsuredSelected,
  isAkaGroupLifeTimeCoInsuredSelected,
  isAkaGroupLifeTimeSelected,
  MappingData,
} from "./mapToCartProducts";

export type AkaGroupLifeProductBackend = {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: InsuranceCoverage;
  memberApplicantPersonInformation?: PersonalApplicantInformation;
  coInsuredApplicantPersonInformation?: PersonalApplicantInformation;
  memberApplicantInsuranceSpecification?: AkaGroupLifeApplicantInsuranceSpecification;
  coInsuredApplicantInsuranceSpecification?: AkaGroupLifeApplicantInsuranceSpecification;
};

export const mapToAkaGroupLifeProduct = (
  data: MappingData,
): AkaGroupLifeProductBackend => ({
  name: INSURANCE_NAME_VALUE.AKA_GROUP_LIFE,
  coverageGroup: getAkaGroupLifeCoverageGroup(data.priceFormData),
  ...mapInsured(data),
  ...mapCoInsured(data),
});

const mapInsured = (
  data: MappingData,
):
  | Pick<
      AkaGroupLifeProductBackend,
      | "memberApplicantPersonInformation"
      | "memberApplicantInsuranceSpecification"
    >
  | undefined => {
  if (!isAkaGroupLifeInsuredSelected(data.priceFormData)) {
    return undefined;
  }

  const { dateOfBirth } = data.customerData.newCustomerResponse;
  if (!isDefined(dateOfBirth)) {
    throw Error(
      "mapToAkaGroupLifeProduct - cannot map co insured cart product - date of birth is not defined",
    );
  }

  const {
    akaGroupLifeDeathGLevel,
    incapacityOfWorkGLevel,
    timeLimitedDisabilityInterestGLevel,
  } = data.priceFormData;

  const memberApplicantInsuranceSpecification: Mutable<
    AkaGroupLifeProductBackend["memberApplicantInsuranceSpecification"]
  > = {};
  if (isAkaGroupLifeDeathSelected(data.priceFormData)) {
    if (!isDefined(akaGroupLifeDeathGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map insured death to cart product - coverage amount g level is not defined",
      );
    }
    memberApplicantInsuranceSpecification.deathInsuranceCoverageGLevel = Number(
      akaGroupLifeDeathGLevel,
    );
  }
  if (isAkaGroupLifeIncapacitySelected(data.priceFormData)) {
    if (!isDefined(incapacityOfWorkGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map insured incapacity of work to cart product - coverage amount g level is not defined",
      );
    }
    memberApplicantInsuranceSpecification.incapacityOfWorkCoverageGLevel =
      Number(incapacityOfWorkGLevel);
  }
  if (isAkaGroupLifeTimeSelected(data.priceFormData)) {
    if (!isDefined(timeLimitedDisabilityInterestGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map insured time limited disability to cart product - coverage amount g level is not defined",
      );
    }
    memberApplicantInsuranceSpecification.timeLimitedDisabilityInterestCoverageGLevel =
      Number(timeLimitedDisabilityInterestGLevel);
  }
  return {
    memberApplicantPersonInformation: {
      dateOfBirth,
    },
    memberApplicantInsuranceSpecification,
  };
};

const mapCoInsured = (
  data: MappingData,
):
  | Pick<
      AkaGroupLifeProductBackend,
      | "coInsuredApplicantPersonInformation"
      | "coInsuredApplicantInsuranceSpecification"
    >
  | undefined => {
  const { addCoInsured } = data.detailsFormData;
  if (
    addCoInsured !== BooleanFormType.TRUE ||
    !isAkaGroupLifeCoInsuredSelected(data.priceFormData)
  ) {
    return undefined;
  }

  const { coInsuredBirthDate } = data.detailsFormData;
  if (!isDefined(coInsuredBirthDate)) {
    throw Error(
      "mapToAkaGroupLifeProduct - cannot map co insured cart product - date of birth is not defined",
    );
  }

  const coInsuredApplicantInsuranceSpecification: Mutable<
    NonNullable<
      AkaGroupLifeProductBackend["coInsuredApplicantInsuranceSpecification"]
    >
  > = {};
  const {
    akaGroupLifeDeathCoInsuredGLevel,
    incapacityOfWorkCoInsuredGLevel,
    timeLimitedDisabilityInterestCoInsuredGLevel,
  } = data.priceFormData;
  if (isAkaGroupLifeDeathCoInsuredSelected(data.priceFormData)) {
    if (!isDefined(akaGroupLifeDeathCoInsuredGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map coinsured death to cart product - coverage amount g level is not defined",
      );
    }
    coInsuredApplicantInsuranceSpecification.deathInsuranceCoverageGLevel =
      Number(akaGroupLifeDeathCoInsuredGLevel);
  }
  if (isAkaGroupLifeIncapacityCoInsuredSelected(data.priceFormData)) {
    if (!isDefined(incapacityOfWorkCoInsuredGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map coinsured incapacity of work to cart product - coverage amount g level is not defined",
      );
    }
    coInsuredApplicantInsuranceSpecification.incapacityOfWorkCoverageGLevel =
      Number(incapacityOfWorkCoInsuredGLevel);
  }
  if (isAkaGroupLifeTimeCoInsuredSelected(data.priceFormData)) {
    if (!isDefined(timeLimitedDisabilityInterestCoInsuredGLevel)) {
      throw Error(
        "mapToAkaGroupLifeProduct - cannot map coinsured time limited disability to cart product - coverage amount g level is not defined",
      );
    }
    coInsuredApplicantInsuranceSpecification.timeLimitedDisabilityInterestCoverageGLevel =
      Number(timeLimitedDisabilityInterestCoInsuredGLevel);
  }

  return {
    coInsuredApplicantPersonInformation: {
      dateOfBirth: format(coInsuredBirthDate, "yyyy-MM-dd"),
    },
    coInsuredApplicantInsuranceSpecification,
  };
};
